import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { required } from '~/app/helpers/validation';

import Loader from '~/app/common/Loader';

import useForm, { UseFormOptionFields } from '~/app/modules/form/useForm';

import { IssueDetails, IssuePayload, Machine, Project } from '~/types';

import { useStoreState } from '~/store';

import { AddIssueProps } from '../';

import Footer from './Footer';
import Categories from './Categories';
import Details from './Details';

import classes from './style.module.scss';
import { IssueTypeRepaired, IssueTypes, SOURCE_ISSUE_TYPE } from '~/app/data/statuses/issue';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import moment from 'moment';
import useAccessControl from '~/app/hooks/useAccessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';


export type FormProps = {
  type: AddIssueProps['type']
  inspectionType?: AddIssueProps['inspectionType']
  onClose(): void
  issueDetails?: IssueDetails
  onSave?: (id: IssueDetails['id']) => void
  sourceTypeId?: AddIssueProps['sourceTypeId']
  allowAddMore?: boolean
}

export type FormComponent = React.FC<FormProps>

const Form: FormComponent = ({ onClose, type, inspectionType, issueDetails, onSave, sourceTypeId, allowAddMore }) => {
  const [showDetails, setShowDetails] = useState<boolean>(issueDetails?.id ? true : false);
  const {checkPermission} = useAccessControl();
  const setIssueRegistrationDatePermission = checkPermission(PERMISSION_GROUPS.setIssueRegistrationDate);

  const { id, projectId } = useParams<{ id: Machine['id'], projectId: Project['id'] }>();
  const currentUserId = useStoreState(state => state.user.data.id);
  const displayTypeInIssues = useStoreState(state => state.machine.displayTypeInIssues);
  const isIssueTypeRequired = useStoreState(state => state.machine.isIssueTypeRequired); 
  
  const formFields = useMemo(() => {
    const fields: UseFormOptionFields = [
      'issueType',
      'machineId',
      'projectId',
      ['categoryId', { validators: [['required', required]] }],
      'categoryName',
      ['description', { validators: [['required', required]] }],
      'images',
      'files',
      type === 'issue' ? ['departmentId', { validators: [['required', required]] }] : 'departmentId',
      type === 'repair' ? ['performedBy', { validators: [['required', required]] }] : 'performedBy',
      displayTypeInIssues && type === 'issue' ? (isIssueTypeRequired ? ['sortingType', { validators: [['required', required]] }] : 'sortingType') : '',
      ['type', { validators: [['required', required]] }],
      'sourceTypeId',
    ];

    !issueDetails?.id && setIssueRegistrationDatePermission && fields.push(['registrationDate', { validators: [['required', required]] }]);
    !!inspectionType !== undefined && fields.push(['inspectionType', {}]);

    return fields;
  }, [displayTypeInIssues, inspectionType, isIssueTypeRequired, issueDetails?.id, setIssueRegistrationDatePermission, type]);

  const { values, defaultValues, removeForm, setValue } = useForm<IssuePayload>({
    formName: 'addIssue',
    fields: formFields,
    defaultValues: {
      ...!issueDetails?.id && setIssueRegistrationDatePermission && {registrationDate: moment().format('YYYY-MM-DDTHH:mm')},
      issueType: type,
      machineId: issueDetails?.machineId ? Number(issueDetails?.machineId) : id,
      projectId: issueDetails?.projectId ? Number(issueDetails?.projectId) : projectId,
      departmentId: issueDetails?.issueDepartmentId ? Number(issueDetails?.issueDepartmentId) : 0,
      performedBy: type === 'repair' && currentUserId ? currentUserId : 0,
      categoryId: issueDetails?.categoryId ? Number(issueDetails?.categoryId) : 0,
      categoryName: issueDetails?.categoryName ?? undefined,
      type: type === 'repair' ? IssueTypeRepaired : (issueDetails?.type ?? IssueTypes.MINOR),
      sortingType: displayTypeInIssues && type === 'issue' && issueDetails?.sortingType ? Number(issueDetails?.sortingType) : 0,
      ...(inspectionType !== undefined && { inspectionType: inspectionType }),
      images: issueDetails?.images ?? [],
      files: issueDetails?.files ?? [],
      description: issueDetails?.description ?? undefined,
      sourceTypeId: sourceTypeId ?? SOURCE_ISSUE_TYPE.FELT_APP,
    }
  });

  const handleAddMore = useCallback(() => {
    if (defaultValues) {
      Object.keys(defaultValues).forEach(key => {
        setValue(key as keyof IssuePayload, defaultValues[key]);
      });
    }
    setShowDetails(false);
  }, [defaultValues, setValue]);

  useDidMountEffect(() => {
    return () => {
      removeForm();
    }
  });

  if (!values) {
    return <Loader size="32"/>;
  }

  return (
    <>
      <div className={classes['container']}>
        {showDetails ? <Details setShowDetails={setShowDetails} type={type} issueDetails={issueDetails} /> : <Categories />}
      </div>

      <Footer onClose={onClose} showDetails={showDetails} setShowDetails={setShowDetails} type={type} issueDetails={issueDetails} defaultFormValues={defaultValues} onSave={onSave} 
        onAddMore={handleAddMore}  allowAddMore={allowAddMore} />
    </>
  );
};

export default Form
